/**
 * Reload once when the running build's chunks are gone.
 *
 * Each deploy removes the last build's hashed assets, so a tab opened before a
 * deploy fails the next time it loads a chunk (the server answers 403). Vite
 * wraps every dynamic import, `React.lazy` ones included, and on a failed load
 * dispatches a cancelable `vite:preloadError` on the window before it throws.
 * The page reloads once to fetch the new build, and cancels the event so the
 * failed import does not throw on the way out.
 *
 * Not again within a short window: a chunk the new build lacks too would
 * otherwise reload for ever. The time of the last reload is kept for the tab in
 * sessionStorage, so it survives the reload it guards. And never over typing
 * that is not saved: the page says a new version is ready instead, and the
 * attorney reloads when they choose.
 */

/** How long after one reload another is refused. */
export const RELOAD_WINDOW_MS = 30_000;

/** Said on the window when the page will not reload itself over unsaved typing. */
export const NEW_BUILD_EVENT = "praxis:new-build";

const RELOADED_AT = "praxis.chunk-reload-at";

export interface ChunkReloadDeps {
  storage: Pick<Storage, "getItem" | "setItem">;
  now: () => number;
  reload: () => void;
  /** Whether any editor on the page holds typing not yet saved. */
  unsaved: () => boolean;
}

/** When this tab last reloaded for a new build; null when never, or when storage is shut. */
function lastReload(storage: ChunkReloadDeps["storage"]): number | null {
  try {
    const held = storage.getItem(RELOADED_AT);
    return held === null ? null : Number(held);
  } catch {
    return null;
  }
}

/** Reload for the new build, unless one was done within the window or typing is unsaved; true when it reloads. */
function reloadForNewBuild(target: Window, deps: ChunkReloadDeps): boolean {
  const last = lastReload(deps.storage);
  if (last !== null && deps.now() - last < RELOAD_WINDOW_MS) return false;
  if (deps.unsaved()) {
    target.dispatchEvent(new Event(NEW_BUILD_EVENT));
    return false;
  }
  try {
    deps.storage.setItem(RELOADED_AT, String(deps.now()));
  } catch {
    // Shut storage cannot guard the loop, so the page does not reload at all.
    return false;
  }
  deps.reload();
  return true;
}

/** Listen for Vite's failed chunk loads on `target`; returns the way to stop. */
export function installChunkReload(
  target: Window,
  deps: ChunkReloadDeps,
): () => void {
  const onPreloadError = (event: Event) => {
    if (reloadForNewBuild(target, deps)) event.preventDefault();
  };
  target.addEventListener("vite:preloadError", onPreloadError);
  return () => {
    target.removeEventListener("vite:preloadError", onPreloadError);
  };
}
